/**
 * 为第三方提供的登录方法
 */
import axios from 'axios';
import { IMSDK, SDK } from '../sdk';
import Domain from '../domain';
import { _log, _error, _report } from './log';
import API from './api';
import { createAid, guid } from './aid.create';

const base64 = require('js-base64/base64.js');

// import { base64 } from '@tencent/qd-lib';
// -------------------interface-------------------
/** 生成vid所需的参数 */
export interface vidConfig {
  /** appid */
  appid: number,
  /** 主号 */
  kfuin: number,
  /** userAgent */
  userAgent?: string,
  /** 额外数据 */
  extendData?: string,
}

/** 缓存的用户信息。 */
export interface vidAccountInput {
  /** 用户的临时id */
  aid: string | number;
  /** aid对应的token */
  d2: string;
  /** 本地生成的aid的配对值 */
  uuid?: string;
}

/** 用户信息。 */
export interface vidAccountOutput {
  /** 用户的临时id */
  aid: string | number;
  /** aid对应的token */
  d2: string;
  /** 鉴权信息，此信息需要在调用imsdk.sessionStart时传入 */
  userInfo?: string;
}



/** 实名鉴权参数 */
export interface vidAuthConf {
  authType: string;
  authData: string;
}

/**
 * 登录数据
 */
export interface LoginParamas {
  /** 通过api获得的用户id */
  aid: string;
  /** 通过api获得的用户d2 */
  d2: string;
  /** 主号*/
  kfuin: number;
  /** appid */
  appid: number;
  /* 企点接待的wpaId，仅私有化方式接入js-SDK的时候才需要传递 */
  wpaId?: string | number;
};

/**
 * im接入参数
 */
export interface IMCreateParams {
  /** instant.js的导出对象，如果不传，会尝试从window上获取。如果是import 'instant.js'方式引入，则必须传入 */
  Instant?: any,
  /** SDK实例。部分情况下需要先在外界生成SDK实例，然后再进行登录处理。如不传，则方法内部会自动生成 */
  imsdk?: SDK,
  /** 渠道类型 */
  channelType?: number;
  /** cos桶值。js-sdk默认为1 */
  cosBucket?: number;
}
// -----------------end interface------------------

/**
 * 创建匿名身份用户 或 验证匿名身份是否有效 或 SSO鉴权返回实名用户是否信息。匿名用户信息创建成功后，业务方应将其保存起来（如存储到localStorage中），以便该用户下次进入后直接获得对应信息。可以将上次保存的匿名用户信息传入进行验证。当传入authConf参数时会进行鉴权处理，account将被忽略。
 * @param config sdk相关配置
 * @param account 已有的用户身份，如果传入则会进行验证并可能更新d2值。
 * @param authConf SSO鉴权参数。因鉴权后，鉴权数据(如code)会被消费掉，为了解决【首次鉴权后用户刷新页面传入已被消费的鉴权数据导致鉴权失败】的问题，需求首次鉴权后将aid+d2临时存储，如sessionStorage。后续传入数据时将存储的aid+d2传入即可。
 * @returns 生成的匿名身份，或者鉴权后的实名身份
 * @example
 * ```ts
 * const {aid, d2} = await SDKUtils.createVisitor();
 * ```
 */
export async function createVisitor(
  config: vidConfig,
  account?: vidAccountInput,
  authConf?: vidAuthConf,
): Promise<vidAccountOutput> {
  let postData: any = {
    uint32_appid: config.appid,
    uint64_kfuin: config.kfuin,
    str_useragent: config.userAgent || '',
    str_extend_data: base64.encode(config.extendData || ''),
  };

  // 鉴权相关参数。即便有aid，也需要带入。
  if (authConf && authConf.authType && authConf.authData) {
    // 鉴权模式
    Object.assign(postData, {
      str_auth_info: base64.encode(authConf.authData)
    });
  }

  // 账号处理。
  let userAccount;
  if (authConf && authConf.authType) {
    // 鉴权模式时，使用外部传入的account 或者 空。
    userAccount = isAvailableAccount(account) ? account : {};
  } else {
    // 匿名模式时，使用传入的用户信息 或者 随机生成本地aid和uuid。
    userAccount = isAvailableAccount(account) ? account : {
      aid: createAid(),
      uuid: guid(),
      d2: '',
    };
  }

  Object.assign(postData, {
    // C侧请求使用的用户key。server根据这个值来确定C侧用户的aid
    // 如果有保存，则返回。否则发送空字符串。
    // 如果有 auth认证， 也传空
    bytes_custom_key: base64.encode(String(userAccount.aid || '')),
    str_session_ticket: (userAccount && userAccount.aid && userAccount.d2) ? userAccount.d2 : '',
    str_client_uuid: (userAccount && userAccount.aid && userAccount.uuid) ? userAccount.uuid : '',
  });

  const IM_DOMAIN = Domain.getSDKDomain();
  _report({
    module: 'c-imsdk-utils',
    method: '[createVisitor][request]',
    message: postData,
  });
  // 2. 从server接口进行校验并获得d2 
  const res = await axios.post(IM_DOMAIN.api + API.LOGIN, postData);
  _report({
    module: 'c-imsdk-utils',
    method: '[createVisitor][response]',
    message: res.data,
  });
  if (res.status !== 200) {
    return Promise.reject({ code: -1, message: 'network error' });
  }
  const { code, data, message } = res.data;
  if (code !== 0 || !data) {
    return Promise.reject({ code: code || -2, message: message || 'api error' });
  }

  const aid = data.uint64_unique_id;
  const d2 = base64.decode(data.session_ticket || '');
  // 鉴权信息
  const userInfo = data.bytes_str_user_info;

  if (!aid || !d2) {
    return Promise.reject({ code: -3, message: 'data error' });
  }

  return { aid, d2, userInfo };
}


/**
 * 登录webim，返回全局唯一的imsdk实例
 * @param params 登录参数
 */
export function loginQDIM(params: LoginParamas, extData: IMCreateParams = {}): SDK {
  let { Instant, imsdk } = extData;
  // 如果外部传入了Instant则使用外部传入的，否则尝试从window获取。
  if (!Instant) {
    Instant = typeof (window) === 'object' && window.Instant;
  }
  
  if (!Instant) {
    _error('Instant must be loaded!');
  }

  // 获得内置域名
  const IM_DOMAIN = Domain.getSDKDomain();
  
  const socketio = Instant.create({
    client_type: 5,       // 固定
    token_type: 0,        // 固定
    skey: '',             // 留空
    appid: params.appid,  // appid
    kfuin: params.kfuin,  // 主号,
    d2: params.d2,        // IM消息系统校验token
    aid: params.aid,      // 用户id
    url: IM_DOMAIN.ws,    // socket url
  });


  if (!imsdk) {
    // 如果外部未传入sdk实例，则获取或创建全局唯一实例。
    imsdk = IMSDK.create();
  }

  // 传入socketio实例
  imsdk.create!({
    socketio,
    Instant
  });

  // 修改channelType
  Object.assign(imsdk.config, {
    channelType: extData.channelType || 50, // 通路类型，SDK通路固定为50
    cosBucket: typeof(extData.cosBucket) !=='undefined' ? extData.cosBucket : 1, // 对外第三方sdk，cosBucket需要传入1
  });

  // 登录，传入其他信息
  imsdk.login!(
    {
      uin: params.aid, // 工号
      corpUin: params.kfuin, // 主号
    },
    {
      wpaId: params.wpaId, // wpaid
    },
  );

  _report({
    module: 'c-imsdk-utils',
    method: '[loginQDIM][done]',
    message: {
      params,
      extData,
    },
  });

  return imsdk;
}

/**
 * 登录webim，同【loginQDIM】，但是每次都会返回新的实例
 * @param params 
 * @returns 
 */
export function newQDIM(params: LoginParamas, extData: IMCreateParams = {}): SDK {
  let { Instant, imsdk } = extData;
  // 如果外部传入了Instant则使用外部传入的，否则尝试从window获取。
  if (!Instant) {
    Instant = typeof (window) === 'object' && window.Instant;
  }
  
  if (!Instant) {
    _error('Instant must be loaded!');
  }

  // 获得内置域名
  const IM_DOMAIN = Domain.getSDKDomain();
  
  const socketio = Instant.newInstance({
    client_type: 5,       // 固定
    token_type: 0,        // 固定
    skey: '',             // 留空
    appid: params.appid,  // appid
    kfuin: params.kfuin,  // 主号,
    d2: params.d2,        // IM消息系统校验token
    aid: params.aid,      // 用户id
    url: IM_DOMAIN.ws,    // socket url
  });

  if (!imsdk) {
    // 如果外部未传入sdk实例，则创建一个新的实例。
    imsdk = IMSDK.new();
  }

  // 传入socketio实例
  imsdk.create!({
    socketio,
    Instant
  });

  // 修改channelType
  Object.assign(imsdk.config, {
    channelType: extData.channelType || 50, // 通路类型，SDK通路固定为50
    cosBucket: typeof(extData.cosBucket) !=='undefined' ? extData.cosBucket : 1, // 对外第三方sdk，cosBucket需要传入1
  });

  // 登录，传入其他信息
  imsdk.login!(
    {
      uin: params.aid, // 工号
      corpUin: params.kfuin, // 主号
    },
    {
      wpaId: params.wpaId, // wpaid
    },
  );

  _report({
    module: 'c-imsdk-utils',
    method: '[newQDIM][done]',
    message: {
      params,
      extData,
    },
  });

  return imsdk;
}

/**
 * 监测account是否有效
 * @param account
 * @returns
 */
function isAvailableAccount(account) {
  const { aid, d2, uuid } = account;
  return (aid && d2) || (aid && uuid);
}